// W2Cook Billing Dashboard — Service definitions
// Edit this file to update the default seed data.
// At runtime, overrides are stored in Firestore (prod) or localStorage (dev).

export type ServiceStatus = "active" | "paused" | "cancelled";

export interface Service {
  id: string;
  name: string;
  category: string;
  environment: "dev" | "staging" | "prod" | "all"; // "all" = applies to all envs
  monthlyCost: number;
  status: ServiceStatus;
  icon: string;
  note: string;
  renewsOn: string | null;
  apiConnectorId: string | null;
  budgetLimit: number;   // 0 = no limit
  createdAt: string;
  updatedAt: string;
}

const now = new Date().toISOString();

export const DEFAULT_SERVICES: Service[] = [
  {
    id: "kiro",
    name: "Kiro Pro Max",
    category: "IDE & Dev Tools",
    monthlyCost: 100,
    status: "active",
    icon: "⚡",
    note: "5,000 credits/mo — planned downgrade to Pro after 6 months",
    environment: "all",
    renewsOn: "2026-08-01",
    budgetLimit: 0,
    apiConnectorId: null,
    createdAt: now, updatedAt: now,
  },
  {
    id: "github-team",
    name: "GitHub Team",
    category: "IDE & Dev Tools",
    monthlyCost: 4,
    status: "active",
    icon: "🐙",
    note: "Org: dhivinslabs-app",
    environment: "all",
    renewsOn: "2026-08-01",
    budgetLimit: 0,
    apiConnectorId: "github",
    createdAt: now, updatedAt: now,
  },
  {
    id: "firebase-dev",
    name: "Firebase Blaze (Dev)",
    category: "Backend & Infrastructure",
    monthlyCost: 0,
    status: "active",
    icon: "🔥",
    note: "Pay-as-you-go — within free tier",
    environment: "dev",
    renewsOn: null,
    budgetLimit: 0,
    apiConnectorId: "gcp",
    createdAt: now, updatedAt: now,
  },
  {
    id: "firebase-staging",
    name: "Firebase Blaze (Staging)",
    category: "Backend & Infrastructure",
    monthlyCost: 0,
    status: "active",
    icon: "🔥",
    note: "Pay-as-you-go — within free tier",
    environment: "staging",
    renewsOn: null,
    budgetLimit: 0,
    apiConnectorId: "gcp",
    createdAt: now, updatedAt: now,
  },
  {
    id: "firebase-prod",
    name: "Firebase Blaze (Prod)",
    category: "Backend & Infrastructure",
    monthlyCost: 0,
    status: "active",
    icon: "🔥",
    note: "Pay-as-you-go — monitor post-launch",
    environment: "prod",
    renewsOn: null,
    budgetLimit: 0,
    apiConnectorId: "gcp",
    createdAt: now, updatedAt: now,
  },
  {
    id: "cloudflare",
    name: "Cloudflare",
    category: "Backend & Infrastructure",
    monthlyCost: 0,
    status: "active",
    icon: "☁️",
    note: "w2cook.com — Free plan",
    environment: "all",
    renewsOn: null,
    budgetLimit: 0,
    apiConnectorId: "cloudflare",
    createdAt: now, updatedAt: now,
  },
  {
    id: "hcp-terraform",
    name: "HCP Terraform",
    category: "Backend & Infrastructure",
    monthlyCost: 0,
    status: "active",
    icon: "🏗️",
    note: "Free tier — IaC for GCP, Cloudflare, GitHub",
    environment: "all",
    renewsOn: null,
    budgetLimit: 0,
    apiConnectorId: "terraform",
    createdAt: now, updatedAt: now,
  },
  {
    id: "revenuecat",
    name: "RevenueCat",
    category: "Backend & Infrastructure",
    monthlyCost: 0,
    status: "active",
    icon: "💰",
    note: "Free up to $2.5k MRR",
    environment: "all",
    renewsOn: null,
    budgetLimit: 0,
    apiConnectorId: "revenuecat",
    createdAt: now, updatedAt: now,
  },
  {
    id: "eas-build",
    name: "EAS Build",
    category: "Mobile & Distribution",
    monthlyCost: 0,
    status: "active",
    icon: "📱",
    note: "Free tier — upgrade to Starter ($19/mo) before App Store submission",
    environment: "all",
    renewsOn: null,
    budgetLimit: 0,
    apiConnectorId: null,
    createdAt: now, updatedAt: now,
  },
  {
    id: "expo-account",
    name: "Expo Account",
    category: "Mobile & Distribution",
    monthlyCost: 0,
    status: "active",
    icon: "🚀",
    note: "Free plan — Starter $19/mo at submission",
    environment: "all",
    renewsOn: null,
    budgetLimit: 0,
    apiConnectorId: null,
    createdAt: now, updatedAt: now,
  },
  {
    id: "apple-dev",
    name: "Apple Developer Program",
    category: "Mobile & Distribution",
    monthlyCost: 8.25,
    status: "active",
    icon: "🍎",
    note: "$99/year individual account — amortized monthly",
    environment: "all",
    renewsOn: "2027-01-01",
    budgetLimit: 0,
    apiConnectorId: null,
    createdAt: now, updatedAt: now,
  },
  {
    id: "google-play",
    name: "Google Play Console",
    category: "Mobile & Distribution",
    monthlyCost: 2.08,
    status: "active",
    icon: "▶️",
    note: "$25 one-time — amortized over 12 months",
    environment: "all",
    renewsOn: null,
    budgetLimit: 0,
    apiConnectorId: null,
    createdAt: now, updatedAt: now,
  },
  {
    id: "sentry",
    name: "Sentry",
    category: "Monitoring & Observability",
    monthlyCost: 0,
    status: "active",
    icon: "🛡️",
    note: "Free tier",
    environment: "all",
    renewsOn: null,
    budgetLimit: 0,
    apiConnectorId: "sentry",
    createdAt: now, updatedAt: now,
  },
  {
    id: "anthropic-api",
    name: "Anthropic API",
    category: "AI & LLM",
    monthlyCost: 0,
    status: "active",
    icon: "🤖",
    note: "Pay-as-you-go — Claude Sonnet 4.6 for all agents",
    environment: "all",
    renewsOn: null,
    budgetLimit: 0,
    apiConnectorId: "anthropic",
    createdAt: now, updatedAt: now,
  },
  {
    id: "zoho",
    name: "Zoho Mail",
    category: "Productivity",
    monthlyCost: 1,
    status: "active",
    icon: "📧",
    note: "w2cook.com email — Mail Lite $1/user/mo",
    environment: "all",
    renewsOn: "2026-08-01",
    budgetLimit: 0,
    apiConnectorId: "zoho",
    createdAt: now, updatedAt: now,
  },
  {
    id: "figma",
    name: "Figma",
    category: "Design",
    monthlyCost: 0,
    status: "active",
    icon: "🎨",
    note: "Free Starter plan — Professional $15/editor/mo if team grows",
    environment: "all",
    renewsOn: null,
    budgetLimit: 0,
    apiConnectorId: "figma",
    createdAt: now, updatedAt: now,
  },
];
